/* eslint-disable max-len */
import React from 'react';
import { useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { Text } from 'common/components/Text';
import { useAppTheme } from 'common/theme/AppTheme';

import { useAvatarStyles } from './Avatar.styles';

interface IAvatarIconProps {
  className: string;
  primaryColor?: string;
  strokeColor?: string;
  /** unique id for the clip path */
  id?: string;
}

// Exported directly from Figma.
// Smaller than icons in fluentui/react-icons.
export const AddIcon = ({ className, primaryColor, strokeColor = '#fff', id }: IAvatarIconProps) => {
  return (
    <svg aria-hidden className={className} xmlns="http://www.w3.org/2000/svg" width="17" height="16" fill="none">
      <rect x="1.729" y="1" width="14" height="14" rx="7" fill={strokeColor} />
      <g clipPath={`url(#${id})`}>
        <path
          d="M2.73 8a6 6 0 1 1 12 0 6 6 0 0 1-12 0Zm6-3a.5.5 0 0 0-.5.5v2h-2a.5.5 0 1 0 0 1h2v2a.5.5 0 0 0 1 0v-2h2a.5.5 0 1 0 0-1h-2v-2a.5.5 0 0 0-.5-.5Z"
          fill={primaryColor}
        />
      </g>
      <rect x="1.729" y="1" width="14" height="14" rx="7" stroke={strokeColor} strokeWidth="2" />
      <defs>
        <clipPath id={id}>
          <path fill={strokeColor} transform="translate(2.73 2)" d="M0 0h12v12H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

// Exported directly from Figma.
// Smaller + has an extra stroke (white outline) compared to
// warning icons from fluentui/react-icons package.
const WarningIcon = ({ className, primaryColor, strokeColor = '#fff' }: Omit<IAvatarIconProps, 'id'>) => {
  return (
    <svg aria-hidden className={className} xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
      <path
        d="M7.99717 3C8.76697 1.66667 10.6915 1.66667 11.4613 3L16.6574 12C17.4272 13.3333 16.465 15 14.9254 15H4.53307C2.99347 15 2.03121 13.3333 2.80101 12L7.99717 3Z"
        fill={strokeColor}
      />
      <path
        d="M4.2906 12.6027L9.29217 3.60269C9.46681 3.28843 9.89567 3.26224 10.1127 3.52412L10.1663 3.60269L15.1678 12.6027C15.3388 12.9103 15.1466 13.283 14.8161 13.3385L14.7308 13.3456H4.72765C4.37571 13.3456 4.1433 12.9965 4.2553 12.6807L4.2906 12.6027L9.29217 3.60269L4.2906 12.6027ZM9.72922 10.2225C9.31476 10.2225 8.97878 10.5585 8.97878 10.9729C8.97878 11.3874 9.31476 11.7234 9.72922 11.7234C10.1437 11.7234 10.4797 11.3874 10.4797 10.9729C10.4797 10.5585 10.1437 10.2225 9.72922 10.2225ZM9.72893 7.22313C9.41513 7.22289 9.15517 7.45395 9.1103 7.7553L9.10345 7.84765L9.10269 8.84509L9.10939 8.93745C9.1538 9.23887 9.41341 9.47033 9.72721 9.47057C10.041 9.47081 10.301 9.23975 10.3458 8.9384L10.3527 8.84604L10.3535 7.84861L10.3467 7.75625C10.3023 7.45483 10.0427 7.22337 9.72893 7.22313Z"
        fill={primaryColor}
      />
    </svg>
  );
};

export interface IAvatarProps {
  /**
   * Required avatar name. This value is used in the
   * avatar image alt text or, if `showName` is `true`,
   * it will be visible
   */
  name: string;
  /**
   * Whether or not the name is visible.
   */
  showName?: boolean;
  /**
   * Required path of the image.
   */
  src: string;
  /**
   * Optional status to apply to the Avatar, which will
   * coorrespond with a small icon in the bottom right of the Avatar.
   */
  status?: 'add' | 'hasAlerts' | undefined;

  width?: number;
  height?: number;
}

export const Avatar: React.FC<IAvatarProps> = (props: IAvatarProps) => {
  const { name, showName = false, src, status, width, height } = props;
  const { appColors, palette } = useAppTheme();
  const styles = useAvatarStyles(width, height);
  const addIconClipPathId = useId();

  let IconComponent: JSX.Element | undefined;

  switch (status) {
    case 'add':
      IconComponent = (
        <AddIcon className={styles.icon} primaryColor={palette.themePrimary} strokeColor={palette.white} id={addIconClipPathId} />
      );
      break;
    case 'hasAlerts':
      IconComponent = (
        <WarningIcon className={styles.icon} primaryColor={appColors?.red20ToCrimson70 || palette.red} strokeColor={palette.white} />
      );
      break;
    default:
      break;
  }

  // Don't use Fluent's Image component
  // because it renders a div wrapper & div elements cannot
  // be descendants of button elements.
  const image = (
    <img
      // Empty alt is valid. Use if showName is true,
      // because the name will be below the image
      // rather than in the alt attribute.
      alt={showName ? '' : name}
      className={clsx(styles.imageStyles, status === 'add' ? styles.fadedImage : '')}
      src={src}
    />
  );

  const text = (
    <Text className={styles.textStyles} nowrap variant="Caption2">
      {name}
    </Text>
  );

  const content = (
    <span className={showName ? styles.container : ''}>
      <span className={styles.imageWrapper}>
        {image}
        {status && IconComponent}
      </span>
      {showName && text}
    </span>
  );

  return content;
};
